<template>
  <DDV
    v-slot="{ enableMouseTracking, onUpdateNearestPoint, mouseState, margins }"
    v-bind="$attrs"
    class="pump-performance"
    :resize-throttle-ms="100"
    @update:dom-state="domState = $event"
  >
    <template v-if="domState">
      <rect
        v-if="domState.plotWidth > 0"
        :x="margins.left"
        :y="margins.top"
        :width="domState.plotWidth"
        :height="domState.plotHeight"
        fill="var(--plotSurface)"
      />
      <template v-if="dataPoints.length">
        <!-- Torque Curve -->
        <CartesianScaler
          v-if="domState.plotWidth > 0"
          v-slot="{ xScaler, yScaler }"
          :x-source="speedRPMDomain"
          :y-source="torqueFtLbsDomain"
          :x-target="targetDomainX"
          :y-target="targetDomainY"
          reverse-y-axis
        >
          <Axis
            :x="margins.left"
            :y="margins.top"
            :domain="speedRPMDomain"
            :label="chartingData?.dataSets?.torque?.xAxisLabel"
            :scaler="xScaler"
            :tick-frequency="ddvTickFrequencies?.x"
            draw-grid-lines
          />
          <Axis
            :x="margins.left"
            :y="margins.top"
            :domain="torqueFtLbsDomain"
            :label="chartingData?.dataSets?.torque?.yAxisLabel"
            :scaler="yScaler"
            :tick-frequency="ddvTickFrequencies?.y"
            is-vertical
            draw-grid-lines
          />
          <g :transform="`translate(${margins.left} ${margins.top})`">
            <LinePlot
              :is-active="activeDataSets[0]"
              :data-series-number="1"
              :screen-points="screenPoints"
              curve-interpolator="straight"
            />
            <BSP
              v-slot="{ nearestPoint }"
              ignore-y-values
              :points="dataPoints"
              :screen-points="screenPoints"
              :track-nearest-point="enableMouseTracking && activeDataSets[0]"
              @update:nearest-point="
                onUpdateNearestPoint(
                  chartingData?.dataSets?.torque?.title || '',
                  1,
                  activeDataSets[0],
                  $event,
                  formatValue,
                  formatValue,
                  `(rpm)`,
                  `(ft-lbs)`
                )
              "
            >
              <NearestPoint
                v-if="nearestPoint?.node"
                :data-series-number="1"
                :nearest-point="nearestPoint"
              />
            </BSP>
            <!-- Operating Point -->
            <g
              v-if="activeOperatingPoints[0] && chartingData?.operatingPoint"
              :transform="`translate(
                ${xScaler.scale(chartingData.operatingPoint.point.x)},
                ${yScaler.scale(chartingData.operatingPoint.point.y)}
              )`"
            >
              <circle
                :cx="0"
                :cy="0"
                :r="5"
                stroke="var(--activePlotColor)"
                :stroke-width="2"
                fill="transparent"
              />
              <circle
                :cx="0"
                :cy="0"
                :r="5"
                stroke="var(--activePlotColor)"
                :stroke-opacity="0.3"
                :stroke-width="8"
                fill="transparent"
              />
              <circle :cx="0" :cy="0" :r="4" fill="var(--plotSurface)" />
            </g>
          </g>
        </CartesianScaler>
      </template>
      <template v-else>
        <text
          fill="var(--plotColor0)"
          x="50%"
          y="50%"
          dominant-baseline="middle"
          text-anchor="middle"
        >
          No Data
        </text>
      </template>

      <ChartTitle :title="title ? `${title}` : 'Speed - Torque'" />

      <Crosshairs
        v-if="readyToDraw(enableMouseTracking, mouseState.isHovered)"
        ignore-y
        :x="margins.left"
        :y="margins.top"
      />
    </template>
  </DDV>
  <div>
    <CustomLegend
      v-if="drawLegend"
      v-model="activeOperatingPoints"
      :model="operatingPoints"
      :static="isStatic"
      :horizontal="isHorizontal"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref } from "vue";
import type { DOMState, NumericPoint } from "@incytestudios/ddv/types/common";
import CustomLegend from "./CustomLegend.vue";
import {
  Axis,
  fromRange,
  DDV,
  BSP,
  LinePlot,
  ChartTitle,
  Crosshairs,
  NearestPoint,
  CartesianScaler,
  getDefaultValueFormatter,
  useScreenPoints,
  useLinearScaler
} from "@incytestudios/ddv";

import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import type { NumericDomain } from "@incytestudios/ddv/types/domain";
import { usePumpSpeedTorqueChartData } from "@/libs/charting/pumpSpeedTorque/usePumpSpeedTorqueChartData";
import { scenarioCalculationsKey } from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { injectStrict } from "@/utils/utils";
import type { OperatingPoint } from "@/components/types";

const props = defineProps<{
  title?: string;
  scenarioCalculations?: ScenarioCalculations;
  drawLegend?: boolean;
  isHorizontal?: boolean;
  isStatic?: boolean;
}>();

const _calcs =
  props.scenarioCalculations || injectStrict(scenarioCalculationsKey);

const readyToDraw = (enableMouseTracking: boolean, isHovered: boolean) => {
  return enableMouseTracking && isHovered && dataPoints.value.length;
};

// Custom pixels to squash the chart
const domState = ref<DOMState>({
  plotWidth: 600,
  plotHeight: 400,
  elementWidth: 600,
  elementHeight: 400,
  computedHeight: 400
});

const ddvTickFrequencies = inject<{ x: string; y: string }>(
  "ddvTickFrequencies"
);

const chartingData = computed(() => {
  return usePumpSpeedTorqueChartData(_calcs);
});

const operatingPoints = computed<OperatingPoint[]>(() => {
  const points: OperatingPoint[] = [];
  if (chartingData.value?.operatingPoint) {
    points.push(chartingData.value.operatingPoint);
  }
  return points;
});

const dataPoints = computed<NumericPoint[]>(() => {
  return (chartingData.value?.dataSets.torque.points || []) as NumericPoint[];
});

const targetDomainX = computed(() => fromRange(domState.value?.plotWidth || 0));
const targetDomainY = computed(() =>
  fromRange(domState.value?.plotHeight || 0)
);

const formatValue = getDefaultValueFormatter();

const activeDataSets = ref([true]);
const activeOperatingPoints = ref([true]);

const speedRPMDomain = computed<NumericDomain>(() => ({
  min: 0,
  max:
    Math.max(
      ...(dataPoints.value || []).map(({ x }) =>
        typeof x === "number" ? x : 0
      )
    ) * 1.1
}));

const torqueFtLbsDomain = computed<NumericDomain>(() => ({
  min: 0,
  max:
    Math.max(
      ...(dataPoints.value || []).map(({ y }) =>
        typeof y === "number" ? y : 0
      )
    ) * 1.1
}));

const speedRPMScaler = useLinearScaler(speedRPMDomain, targetDomainX, false);
const torqueFtLbsScaler = useLinearScaler(
  torqueFtLbsDomain,
  targetDomainY,
  true
);

const { screenPoints } = useScreenPoints(
  dataPoints,
  speedRPMScaler,
  torqueFtLbsScaler
);
</script>

<style lang="scss" scoped></style>
